import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";

const filterK2PortfoliosSchema = z.object({
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  reporting_currency_contains: z.string().optional(),
  bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  account_type_contains: z.string().optional(),
  investment_type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  contract_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  collateral_contains: z.string().optional(),
});

export type FilterK2PortfoliosSchema = z.infer<typeof filterK2PortfoliosSchema>;

const portfolioDepFields: readonly DepField<FilterK2PortfoliosSchema>[] = [
  {
    name: "bank_name_contains",
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "client_contains",
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "vehicle_contains",
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "investment_type_contains",
    key: "investment_type",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "contract_name_contains",
    key: "contract_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "code_contains",
    key: "code",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "account_name_contains",
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

const fields: Field[] = [
  {
    name: "client_contains",
    label: `Client`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "client",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "client_contains",
    ),
  },
  {
    name: "vehicle_contains",
    label: `Véhicule / Bénéficiaire`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "vehicle",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "vehicle_contains",
    ),
  },
  {
    name: "account_name_contains",
    label: `Groupe de comptes`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "account_name",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "account_name_contains",
    ),
  },
  {
    name: "name_contains",
    label: `Compte`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "name",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "name_contains",
    ),
  },
  {
    name: "currency_contains",
    label: `Devise du portefeuille`,
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "reporting_currency_contains",
    label: `Devise de reporting`,
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "bank_name_contains",
    label: `Banque`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "bank_name",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "bank_name_contains",
    ),
  },
  {
    name: "account_type_contains",
    label: `Type de compte`,
    type: "select",
    useTranslation: true,
    client: "k2",
    table_name: "k2_portfolios",
    language: "fr",
    translate_key: "account_type",
  },
  {
    name: "investment_type_contains",
    label: `Type d'investissement`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "investment_type",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "investment_type_contains",
    ),
  },
  {
    name: "contract_name_contains",
    label: `Nom du contrat`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "contract_name",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "contract_name_contains",
    ),
  },
  {
    name: "code_contains",
    label: `Code`,
    type: "asyncAutocomplete",
    base_url: "k2/k2_portfolios",
    column_name: "code",
    filters: makeFiltersBuilder<FilterK2PortfoliosSchema>(
      portfolioDepFields,
      "code_contains",
    ),
  },
  {
    name: "collateral_contains",
    label: `Compte-titres nanti`,
    type: "select",
    useTranslation: false,
    options: [
      { value: "true", label: "Oui" },
      { value: "false", label: "Non" },
    ],
  },
];

const client = "k2";

export function FilterK2Portfolios() {
  return (
    <FilterTable
      client={client}
      schema={filterK2PortfoliosSchema}
      tableName="k2_portfolios"
      fields={fields}
      columns={2}
    />
  );
}
